/* global Word, console */
import { useEffect, useCallback } from "react";
import Fuse from "fuse.js";

// Module-level variables for shared state (Singleton pattern)
let fuseInstance: Fuse<{ text: string; index: number }> | null = null;
let isDirty = true;
let indexingPromise: Promise<void> | null = null;

export const useFuzzySearch = () => {
  const buildIndex = useCallback((): Promise<void> => {
    if (indexingPromise) return indexingPromise;

    indexingPromise = (async () => {
      try {
        await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          context.load(paragraphs, "text");
          await context.sync();

          const paragraphTexts = paragraphs.items.map((p, index) => ({
            text: p.text,
            index: index,
          }));

          fuseInstance = new Fuse(paragraphTexts, {
            keys: ["text"],
            includeScore: true,
            includeMatches: true,
            threshold: 0.4,
            ignoreLocation: true,
          });

          isDirty = false;
        });
      } catch (error) {
        console.error("Error building fuzzy search index:", error);
      } finally {
        indexingPromise = null;
      }
    })();

    return indexingPromise;
  }, []);

  useEffect(() => {
    // Listener may have been detached previously; force a rebuild on remount
    isDirty = true;

    let eventHandler: OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs> | null = null;
    let trackedDocument: Word.Document | null = null;
    let disposed = false;

    const setupListener = async () => {
      // Build index immediately on load if needed
      if (isDirty && !fuseInstance) {
        await buildIndex();
      }

      try {
        await Word.run(async (context) => {
          trackedDocument = context.document;
          eventHandler = context.document.onParagraphChanged.add(() => {
            isDirty = true;
            return Promise.resolve();
          });
          await context.sync();
        });
      } catch (error) {
        console.error("Failed to setup document change listener:", error);
        return;
      }

      // Unmount happened while registration was in-flight — clean up immediately
      if (disposed && eventHandler && trackedDocument) {
        Word.run(trackedDocument, async (context) => {
          eventHandler!.remove();
          await context.sync();
        }).catch(console.error);
      }
    };

    setupListener();

    return () => {
      disposed = true;
      isDirty = true;
      if (eventHandler && trackedDocument) {
        Word.run(trackedDocument, async (context) => {
          eventHandler!.remove();
          await context.sync();
        }).catch(console.error);
      }
    };
  }, [buildIndex]);

  const fuzzySearchInDocument = async (text: string): Promise<boolean> => {
    try {
      if (isDirty || !fuseInstance) {
        await buildIndex();
      }

      const results = fuseInstance?.search(text) || [];

      if (results.length === 0) return false;

      const bestMatch = results[0]!;

      return await Word.run(async (context) => {
        // Use text-based search rather than paragraph index to avoid stale
        // ordering if the document changed since the index was built.

        // Try to find the specific matched substring first
        if (bestMatch.matches && bestMatch.matches.length > 0) {
          const firstMatch = bestMatch.matches[0];
          if (firstMatch) {
            const indices = firstMatch.indices;

            // Find the longest contiguous match
            let longestMatch = { start: 0, end: 0, length: 0 };

            indices.forEach(([start, end]) => {
              const length = end - start + 1;
              if (length > longestMatch.length) {
                longestMatch = { start, end, length };
              }
            });

            // Only use the substring if it's substantial (e.g., > 3 chars)
            if (longestMatch.length > 3) {
              const matchedText = bestMatch.item.text.substring(
                longestMatch.start,
                longestMatch.end + 1
              );

              const searchResults = context.document.body.search(matchedText, {
                ignorePunct: true,
                ignoreSpace: true,
              });
              context.load(searchResults, "items");
              await context.sync();

              if (searchResults.items.length > 0) {
                searchResults.items[0]?.select();
                await context.sync();
                return true;
              }
            }
          }
        }

        // Fallback: search for the full paragraph text
        const fallbackResults = context.document.body.search(bestMatch.item.text, {
          ignorePunct: false,
          ignoreSpace: false,
        });
        context.load(fallbackResults, "items");
        await context.sync();

        if (fallbackResults.items.length > 0) {
          fallbackResults.items[0]?.select();
          await context.sync();
          return true;
        }

        return false;
      });
    } catch (error) {
      console.error("Error in fuzzySearchInDocument:", error);
      return false;
    }
  };

  return { fuzzySearchInDocument, buildIndex };
};
